import { Search } from "@mui/icons-material";
import {
  CircularProgress,
  InputAdornment,
  Popover,
  Popper,
  TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { FaBars, FaRegBell } from "react-icons/fa";
import { IoMdPaper } from "react-icons/io";
import { IoBasketOutline } from "react-icons/io5";
import { useProSidebar } from "react-pro-sidebar";
import { useNavigate, useParams } from "react-router-dom";
import {
  deleteHistorySearch,
  deleteSingleHistorySearch,
  getCartDetails,
  getHistorySearch,
  getNotificationPurchase,
  readNotificationPurchase,
  searchKeywordProduct,
} from "../../../../services/purchaseApi";
import EmptyList from "../../../ui/EmptyList";
import useDebounce from "../../../hooks/useDebounce";
import { RxCross1 } from "react-icons/rx";
import moment from "moment";
import { UseCartQueryPbf } from "../../../hooks/purchaseHook/useCartQueryPbf";

const toIDR = (val) =>
  val?.toLocaleString("id-ID", {
    style: "currency",
    currency: "IDR",
  });

const PurchaseHeader = ({ searchRef }) => {
  const { toggleSidebar, broken } = useProSidebar();
  const { listType } = useParams();

  const navigate = useNavigate();
  const inputRef = useRef(null);
  const popperRef = useRef(null);

  useImperativeHandle(searchRef, () => ({
    focus: () => {
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
      }
    },
  }));

  const [anchorElCart, setAnchorElCart] = useState(null);
  const [anchorElNotif, setAnchorElNotif] = useState(null);
  const [openSearch, setOpenSearch] = useState(null);
  const [search, setSearch] = useState("");
  const [hoverId, setHoverId] = useState(null);

  const debounce = useDebounce(search, 800);

  const getDiscount = (product) => {
    return product.discount?.percent;
  };

  const {
    data: dataHistorySearch,
    isFetching: isFetchingHistorySearch,
    refetch: refetchHistorySearch,
  } = useQuery({
    queryKey: ["history-search", openSearch],
    queryFn: () => getHistorySearch(),
    enabled: openSearch === true,
    refetchOnWindowFocus: false,
  });

  const {
    mutate: mutateRecommendation,
    data: dataRecommendation,
    isLoading: isLoadingRecommendation,
  } = useMutation({
    mutationFn: searchKeywordProduct,
  });

  const {
    mutate: mutateDeleteHistorySearch,
    isLoading: isLoadingDeleteHistorySearch,
  } = useMutation({
    mutationFn: deleteHistorySearch,
    onSuccess: () => refetchHistorySearch(),
  });

  const {
    mutate: mutateDeleteSingleHistorySearch,
    isLoading: isLoadingDeleteSingleHistorySearch,
  } = useMutation({
    mutationFn: deleteHistorySearch,
    onSuccess: () => refetchHistorySearch(),
  });

  const {
    mutate: mutateNotification,
    data: dataNotification,
    isLoading: isLoadingNotification,
  } = useMutation({
    mutationFn: getNotificationPurchase,
  });

  const { isFetching: isFetchingRead } = useQuery({
    queryKey: ["read-notification", dataNotification],
    queryFn: () => readNotificationPurchase(),
    enabled: !isLoadingNotification && !!anchorElNotif,
    refetchOnWindowFocus: false,
  });

  const { data: dataCart, isFetching: isFetchingCart } = UseCartQueryPbf();

  useEffect(() => {
    if (debounce !== "")
      mutateRecommendation({
        keyword: debounce,
        limit: 0,
      });
  }, [debounce]);

  useEffect(() => {
    if (listType && listType.includes("keyword=")) {
      setSearch(listType.split("=")[1]);
    }
  }, [listType]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current?.parentNode &&
        !inputRef.current.parentNode.contains(event.target) && // Click outside TextField
        popperRef.current &&
        !popperRef.current.contains(event.target) // Click outside Popper
      ) {
        setOpenSearch(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div className="bg-[#071B82] text-[12px] text-[#454257] fixed top-0 left-0 lg:left-[260px] h-[70px] px-[20px] py-[10px] w-full lg:w-[calc(100%-260px)] text-[12px] flex items-center z-50">
        <div className="flex items-center justify-between items-center w-full gap-4">
          <div className="w-full">
            <TextField
              autoComplete="off"
              inputRef={inputRef}
              fullWidth
              placeholder="Cari di OLIN"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "12px" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  navigate(
                    `/purchase-pbf/product-list/keyword=${e.target.value}`,
                  );
                  setOpenSearch(false);
                }
              }}
              onFocus={(e) => setOpenSearch(true)}
              onClick={() => setOpenSearch(true)}
            />
          </div>
          <div className="relative">
            <FaRegBell
              className="text-[24px] cursor-pointer hover:text-gray-200 text-white"
              onClick={(e) => {
                setAnchorElNotif(e.currentTarget);
                mutateNotification();
              }}
            />
            {dataNotification &&
            !isLoadingNotification &&
            dataNotification.filter((item) => !item.is_read).length > 0 ? (
              <div className="bg-red-500 text-white w-fit min-w-[18px] h-[18px] rounded-full absolute -top-1 -right-4 p-[8px] flex justify-center items-center translate-x-[2px] -translate-y-[4px]">
                {dataNotification.filter((item) => !item.is_read).length}
              </div>
            ) : null}
          </div>
          <div className="relative">
            <IoMdPaper
              className="text-[28px] cursor-pointer hover:text-gray-200 text-white"
              onClick={() => navigate(`/purchase-pbf/list-transaction/0`)}
            />
          </div>
          <div className="relative">
            <IoBasketOutline
              className="text-[32px] cursor-pointer hover:text-gray-200 mr-4 text-white"
              onClick={(e) => setAnchorElCart(e.currentTarget)}
            />
            {dataCart &&
            dataCart.items &&
            !isFetchingCart &&
            dataCart.items.length > 0 ? (
              <div className="bg-red-500 text-white w-fit min-w-[18px] h-[18px] rounded-full absolute top-0 right-0 p-[8px] flex justify-center items-center translate-x-[2px] -translate-y-[4px]">
                {dataCart.items.length}
              </div>
            ) : null}
          </div>
          {broken ? (
            <button onClick={() => toggleSidebar()}>
              <FaBars className="text-[24px] text-white cursor-pointer hover:text-gray-200" />
            </button>
          ) : null}
        </div>
      </div>

      <Popper
        ref={popperRef}
        open={openSearch}
        anchorEl={inputRef.current?.parentNode}
        placement="bottom-start"
        sx={{
          width: inputRef.current?.parentNode?.offsetWidth || "auto",
          zIndex: 100,
        }}
      >
        <div className="bg-white rounded-md shadow-lg">
          {dataHistorySearch &&
          !isFetchingHistorySearch &&
          !isLoadingDeleteHistorySearch &&
          !isLoadingDeleteSingleHistorySearch ? (
            <>
              <div>
                {isLoadingRecommendation ? (
                  <div className="py-2 px-4">
                    <CircularProgress size={20} />
                  </div>
                ) : debounce !== "" &&
                  (!(listType && listType.includes("keyword=")) ||
                    (listType &&
                      listType.includes("keyword=") &&
                      debounce !== listType.split("=")[1])) &&
                  dataRecommendation &&
                  dataRecommendation.data ? (
                  <>
                    {dataRecommendation.data?.length > 0 ? (
                      dataRecommendation.data?.map((item, i) => {
                        return (
                          <div
                            className={`flex gap-4 items-center py-2 px-4 cursor-pointer hover:bg-gray-100 ${
                              i === 0 ? "rounded-t-md" : ""
                            }`}
                            key={i}
                            onClick={() => {
                              navigate(
                                `/purchase-pbf/product-list/keyword=${item.name}`,
                              );
                              setOpenSearch(false);
                            }}
                          >
                            <div className="flex gap-4 items-center">
                              <Search /> <p>{item.name}</p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="px-4 pt-4 text-[12px] text-gray-400">
                        Tidak ada hasil pencarian{" "}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mb-2 flex justify-between items-center px-4 pt-2">
                      <p className="text-[16px] font-semibold">
                        Pencarian Terakhir
                      </p>
                      <p
                        className="cursor-pointer hover:text-red-500 text-[12px]"
                        onClick={() => mutateDeleteHistorySearch(0)}
                      >
                        Hapus semua
                      </p>
                    </div>
                    {dataHistorySearch.search_history &&
                    dataHistorySearch.search_history.length > 0 ? (
                      dataHistorySearch.search_history
                        .slice(0, 5)
                        .map((item, i) => (
                          <div
                            className={`flex gap-4 items-center justify-between py-2 px-4 text-[12px] ${
                              i === 0 ? "rounded-t-md" : ""
                            } ${
                              hoverId === item.id
                                ? "cursor-pointer hover:bg-gray-100"
                                : ""
                            }`}
                            key={i}
                          >
                            <div
                              className="flex gap-4 items-center w-full"
                              onMouseEnter={() => setHoverId(item.id)}
                              onMouseLeave={() => setHoverId(null)}
                              onClick={() => {
                                navigate(
                                  `/purchase-pbf/product-list/keyword=${item.keyword}`,
                                );
                                setOpenSearch(false);
                              }}
                            >
                              <p>{item.keyword}</p>
                            </div>
                            <div
                              className="cursor-pointer hover:text-red-500 text-[14px] w-fit"
                              onClick={() =>
                                mutateDeleteSingleHistorySearch(item.id)
                              }
                            >
                              <RxCross1 />
                            </div>
                          </div>
                        ))
                    ) : (
                      <p className="py-2 px-4 text-[12px]">
                        Tidak ada history produk
                      </p>
                    )}
                  </>
                )}
              </div>
              <hr className="my-2" />
              <div className="px-4 pb-4">
                <p className="text-[16px] font-semibold mb-2 ">
                  Terakhir Dilihat
                </p>
                <div className="flex gap-3 items-center">
                  {dataHistorySearch.view_history ? (
                    dataHistorySearch.view_history
                      .slice(0, 5)
                      .map((item, i) => (
                        <div
                          className={`w-[100px] h-[100px] cursor-pointer hover:shadow-lg`}
                          key={i}
                          onClick={() =>
                            navigate(
                              `/purchase-pbf/detail-product/${item.slug}/${item.tipe ? item.tipe : 1}`,
                            )
                          }
                        >
                          <img
                            src={item.image}
                            className="h-full w-full object-contain rounded-md border-2"
                          />
                        </div>
                      ))
                  ) : (
                    <>Tidak ada history produk</>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="h-[50px] px-2 py-3">
              {isLoadingDeleteHistorySearch ||
              isLoadingDeleteSingleHistorySearch ||
              isFetchingHistorySearch ? (
                <CircularProgress size={20} />
              ) : null}
            </div>
          )}
        </div>
      </Popper>

      <Popover
        open={Boolean(anchorElNotif)}
        anchorEl={anchorElNotif}
        onClose={() => {
          setAnchorElNotif(null);
          // refetchNotification();
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ marginTop: 1, minWidth: "20vw" }}
      >
        <div className="w-full w-[50vw] lg:w-[20vw] text-[#2C3E5D] max-h-[55vh] overflow-y-auto">
          {dataNotification &&
          !isLoadingNotification &&
          dataNotification.length > 0 ? (
            dataNotification.map((item, i) => {
              return (
                <div
                  className={`${i === 0 ? "" : "border-t"} ${
                    item.is_read === true ? "bg-gray-200" : ""
                  } px-4 py-2 text-wrap hover:bg-gray-300 cursor-pointer`}
                  onClick={() => {
                    if (item.type === 1 || item.type === 3)
                      navigate(
                        `/online-billing-pbf/detail/${item.relational_id}`,
                      );
                    if (item.type === 1 || item.type === 4)
                      navigate(`/oms/0/${item.relational_id}`);
                    else navigate(`/sfa/detail/${item.relational_id}`);
                  }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <p className="font-semibold">{item.title}</p>
                    <p>{moment(item.created_at).format("DD/MM/YYYY")}</p>
                  </div>
                  {item.description}
                </div>
              );
            })
          ) : isLoadingNotification ? (
            <div className="my-2 flex justify-center p-4 ">
              <CircularProgress size={20} />{" "}
            </div>
          ) : (
            <p className="text-center p-4">Tidak ada notifikasi</p>
          )}
        </div>
      </Popover>

      <Popover
        open={Boolean(anchorElCart)}
        anchorEl={anchorElCart}
        onClose={() => setAnchorElCart(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ marginTop: 1, minWidth: "30vw" }}
      >
        <div className="w-full min-w-[50vw] lg:min-w-[30vw] p-4 text-[#2C3E5D] max-h-[55vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4 gap-8 absolute top-0 left-0 w-full p-4 bg-white h-[40px]">
            <p className="text-[16px] cursor-default">
              Keranjang (
              {dataCart && dataCart.items ? dataCart.items.length : 0} item)
            </p>
            <p
              className="cursor-pointer hover:text-gray-300"
              onClick={() => navigate(`/purchase-pbf/cart`)}
            >
              Lihat semua
            </p>
          </div>

          <div className="mt-[40px]">
            {dataCart && dataCart.items && !isFetchingCart ? (
              // {false ? (
              (dataCart.items.length > 10
                ? dataCart.items.slice(0, 10)
                : dataCart.items
              )?.map((item, i) => {
                return (
                  <div
                    className={`flex gap-4 items-center ${
                      i === 0 ? "" : "mt-3"
                    }`}
                  >
                    <img
                      src={item.image}
                      className="w-[50px] h-[50px] object-contain rounded-md border"
                    />
                    <div className="w-full">
                      <p>{item.name}</p>
                      <p className="text-gray-500">
                        {item.qty} x{" "}
                        {toIDR(
                          item.discount && item.discount?.percent > 0
                            ? item.unit_price *
                                ((100 - getDiscount(item)) / 100)
                            : item.unit_price,
                        )}
                      </p>
                    </div>
                    <div className="w-fit text-red-500 font-bold text-right">
                      {toIDR(item.total_price)}
                    </div>
                  </div>
                );
              })
            ) : isFetchingCart ? (
              <CircularProgress size={20} />
            ) : (
              <EmptyList
                message={
                  <div className="text-center">
                    <p className="text-xl align-center mt-8 mb-1">
                      Keranjang Masih Kosong
                    </p>
                    <p className="text-[12px] mb-3">
                      Ayo mulai cari barang yang kamu butuhkan
                    </p>
                    <button
                      type="button"
                      className="text-white bg-[#94C679] hover:bg-gray-300 rounded-md w-full p-2 text-[14px]"
                      onClick={() => setAnchorElCart(null)}
                    >
                      Mulai Belanja
                    </button>
                  </div>
                }
              />
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default PurchaseHeader;
